import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CustomsDeclarationView, FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { declaredValueChanged, declaredValueErrors } from '../components/DeclaredValueFields';
import type { DeclaredValueDraft } from '../components/DeclaredValueFields';

interface CustomsEdit {
  lineNo: number;
  draft: DeclaredValueDraft;
  storedAmount: number | null;
}

interface RebookConfirmation {
  title: string;
  message: string;
  confirmLabel: string;
}

const errorMessage = (error: unknown): string => {
  const body = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data;
  return Array.isArray(body?.message)
    ? body.message.join(' · ')
    : body?.message ||
        (error instanceof Error ? error.message : 'Could not update the customs declaration. Try again.');
};

const needsReconfirmation = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: string; rule?: string } } })?.response;
  return (
    response?.status === 409 &&
    ['CARRIER_REBOOK_CONFIRMATION_REQUIRED', 'SOURCE_SHIPMENT_CHANGED'].includes(
      response.data?.code ?? response.data?.rule ?? '',
    )
  );
};

/** Dedicated customs reads/writes never pass through the sale amount or line metadata APIs. */
export function useCustomsDeclaration(
  client: FulfillmentAdminClient,
  orderId: number,
  refreshKey: unknown,
  confirm: (question: RebookConfirmation) => Promise<boolean>,
) {
  const [view, setView] = useState<CustomsDeclarationView | null>(null);
  const [loadError, setLoadError] = useState('');
  const [edit, setEdit] = useState<CustomsEdit | null>(null);
  const [preview, setPreview] = useState<CustomsDeclarationView | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [writeError, setWriteError] = useState('');
  const [retry, setRetry] = useState(0);
  const currentOrder = useRef(orderId);
  useLayoutEffect(() => {
    currentOrder.current = orderId;
  }, [orderId]);
  const session = useRef(0);
  const savingRef = useRef(false);
  const readRevision = useRef(0);
  const unconfirmedWrite = useRef(false);

  const applyCommittedView = useCallback((data: CustomsDeclarationView) => {
    const reconcileEdit = unconfirmedWrite.current;
    unconfirmedWrite.current = false;
    setLoadError('');
    setView(data);
    if (reconcileEdit) {
      setPreview(null);
      setEdit((current) => {
        const line = current && data.lines.find((entry) => entry.lineNo === current.lineNo);
        return current && line ? { ...current, storedAmount: line.declaredValueOverride } : current;
      });
    }
  }, []);

  useEffect(() => {
    session.current += 1;
    unconfirmedWrite.current = false;
    setView(null);
    setEdit(null);
    setPreview(null);
    setError('');
    setWriteError('');
  }, [orderId]);

  useEffect(() => {
    if (!Number.isInteger(orderId) || orderId <= 0) {
      return;
    }
    let ignore = false;
    const revision = ++readRevision.current;
    setLoadError('');
    client
      .getCustomsDeclaration(orderId)
      .then((data) => {
        if (!ignore && revision === readRevision.current) {
          applyCommittedView(data);
        }
      })
      .catch(() => {
        if (!ignore && revision === readRevision.current) {
          setLoadError('Customs declaration unavailable. Retry before editing declared values.');
        }
      });
    return () => {
      ignore = true;
    };
  }, [client, orderId, refreshKey, retry, applyCommittedView]);

  const beginEdit = useCallback(
    (lineNo: number) => {
      session.current += 1;
      setError('');
      setWriteError('');
      setPreview(null);
      const line = view?.lines.find((entry) => entry.lineNo === lineNo);
      setEdit(
        line
          ? {
              lineNo,
              storedAmount: line.declaredValueOverride,
              draft: {
                amount: line.declaredValueOverride == null ? '' : String(line.declaredValueOverride),
                reason: '',
              },
            }
          : null,
      );
    },
    [view],
  );

  const endEdit = useCallback(() => {
    session.current += 1;
    setEdit(null);
    setPreview(null);
    setError('');
    setWriteError('');
  }, []);

  const changed = edit !== null && declaredValueChanged(edit.draft, edit.storedAmount);
  const errors = edit && changed ? declaredValueErrors(edit.draft) : {};
  const amount = edit?.draft.amount.trim() ?? '';
  const lineNo = edit?.lineNo;

  useEffect(() => {
    setPreview(null);
    setError('');
    if (!changed || lineNo === undefined || errors.amount) {
      setPreviewing(false);
      return;
    }
    let ignore = false;
    setPreviewing(true);
    const timeout = setTimeout(() => {
      client
        .previewCustomsDeclaration(orderId, { lineNo, declaredValueOverride: amount === '' ? null : Number(amount) })
        .then((data) => {
          if (!ignore) {
            setPreview(data);
          }
        })
        .catch((failure: unknown) => {
          if (!ignore) {
            setError(errorMessage(failure));
          }
        })
        .finally(() => {
          if (!ignore) {
            setPreviewing(false);
          }
        });
    }, 300);
    return () => {
      ignore = true;
      clearTimeout(timeout);
    };
  }, [client, orderId, lineNo, amount, changed, errors.amount]);

  const updateDraft = (draft: DeclaredValueDraft) => {
    setEdit((previous) => (previous ? { ...previous, draft } : null));
    setError('');
    setWriteError('');
  };

  /** Always preview again at save time; a carrier created since Edit opened must be confirmed too. */
  const save = async (): Promise<boolean> => {
    if (!changed || !edit) {
      return true;
    }
    if (savingRef.current || loadError || unconfirmedWrite.current || Object.keys(errors).length > 0) {
      return false;
    }
    savingRef.current = true;
    // A read begun before the write must not replace its response with the old declaration.
    readRevision.current += 1;
    setSaving(true);
    setError('');
    setWriteError('');
    const targetSession = session.current;
    const stillHere = () => currentOrder.current === orderId && session.current === targetSession;
    const declaredValueOverride = edit.draft.amount.trim() === '' ? null : Number(edit.draft.amount);
    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const prospective = await client.previewCustomsDeclaration(orderId, {
          lineNo: edit.lineNo,
          declaredValueOverride,
        });
        if (!stillHere()) {
          return false;
        }
        setPreview(prospective);
        if (prospective.carrierOrderExists) {
          if (!prospective.carrierOrderId) {
            throw new Error('Carrier order ID missing. Refresh before saving.');
          }
          const agreed = await confirm({
            title: 'Rebook carrier for customs change',
            message: `Saving this customs value will cancel carrier order ${prospective.carrierOrderId} and rebook it with the new declaration. Declared total: ${prospective.currency} ${prospective.declaredTotal.toFixed(2)}. Sale amounts will not change.${prospective.belowAmountPaidWarning ? ' Warning: the declared total is below what the customer paid.' : ''}`,
            confirmLabel: 'Save customs value and rebook',
          });
          if (!agreed || !stillHere()) {
            return false;
          }
        }
        try {
          const saved = await client.setLineDeclaredValue(orderId, edit.lineNo, {
            declaredValueOverride,
            reason: edit.draft.reason.trim(),
            ...(prospective.carrierOrderExists && prospective.carrierOrderId
              ? {
                  confirmRebook: true,
                  expectedCarrierOrderId: prospective.carrierOrderId,
                }
              : {}),
          });
          if (!stillHere()) {
            return false;
          }
          readRevision.current += 1;
          setView(saved);
          setEdit({ ...edit, storedAmount: declaredValueOverride, draft: { ...edit.draft, reason: '' } });
          setPreview(null);
          return true;
        } catch (failure) {
          if (!needsReconfirmation(failure)) {
            if (stillHere()) {
              // PATCH can commit the correction before carrier reset fails. Never retry an unconfirmed view.
              unconfirmedWrite.current = true;
              const revision = ++readRevision.current;
              setPreview(null);
              setWriteError(
                `The customs update may already be saved. ${errorMessage(failure)} Check the refreshed declaration, audit and carrier status before retrying.`,
              );
              try {
                const latest = await client.getCustomsDeclaration(orderId);
                if (stillHere() && revision === readRevision.current) {
                  applyCommittedView(latest);
                }
              } catch {
                if (stillHere() && revision === readRevision.current) {
                  setView(null);
                  setLoadError('Customs declaration unavailable. Retry before editing declared values.');
                }
              }
            }
            throw failure;
          }
          const latest = await client.getCustomsDeclaration(orderId);
          if (!stillHere()) {
            return false;
          }
          readRevision.current += 1;
          setView(latest);
          if (attempt === 1) {
            setError('The carrier shipment changed again. Save again to review and confirm the latest rebooking.');
            return false;
          }
        }
      }
      return false;
    } catch (failure) {
      if (stillHere()) {
        setError(errorMessage(failure));
      }
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return {
    view,
    loadError: unconfirmedWrite.current
      ? 'The customs update may already be saved. Reload the customs declaration before retrying.'
      : loadError,
    retry: () => setRetry((value) => value + 1),
    edit,
    changed,
    errors,
    preview,
    previewing,
    saving,
    error,
    writeError,
    beginEdit,
    endEdit,
    updateDraft,
    save,
  };
}
